<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  options: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyText: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

// Acima disso a lista deixa de ser confortavel de percorrer com o olho.
const SEARCH_THRESHOLD = 8;

const isOpen = ref(false);
const searchQuery = ref('');
const activeIndex = ref(-1);
const rootRef = ref(null);
const triggerRef = ref(null);
const searchRef = ref(null);

const showSearch = computed(() => props.options.length > SEARCH_THRESHOLD);

const selectedOption = computed(() =>
  props.options.find(option => option.value === props.modelValue)
);

const triggerLabel = computed(
  () => selectedOption.value?.label || props.placeholder
);

const filteredOptions = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  if (!query) return props.options;
  return props.options.filter(option =>
    String(option.label).toLocaleLowerCase().includes(query)
  );
});

const onDocumentPointerDown = event => {
  if (!rootRef.value?.contains(event.target)) isOpen.value = false;
};

// Captura antes do modal: Escape com a lista aberta fecha so a lista.
const onDocumentKeydown = event => {
  if (event.key !== 'Escape') return;
  event.stopPropagation();
  isOpen.value = false;
  triggerRef.value?.focus();
};

const unbindGlobalListeners = () => {
  document.removeEventListener('mousedown', onDocumentPointerDown);
  document.removeEventListener('keydown', onDocumentKeydown, true);
};

const bindGlobalListeners = () => {
  document.addEventListener('mousedown', onDocumentPointerDown);
  document.addEventListener('keydown', onDocumentKeydown, true);
};

watch(isOpen, opened => {
  if (opened) {
    activeIndex.value = props.options.findIndex(
      option => option.value === props.modelValue
    );
    bindGlobalListeners();
    nextTick(() => {
      if (showSearch.value) searchRef.value?.focus();
    });
    return;
  }

  searchQuery.value = '';
  activeIndex.value = -1;
  unbindGlobalListeners();
});

const toggle = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

const selectOption = option => {
  if (option.disabled) return;
  emit('update:modelValue', option.value);
  isOpen.value = false;
  triggerRef.value?.focus();
};

const moveActive = step => {
  const total = filteredOptions.value.length;
  if (!total) return;
  activeIndex.value = (((activeIndex.value + step) % total) + total) % total;
};

const onTriggerKeydown = event => {
  if (!isOpen.value) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      isOpen.value = true;
    }
    return;
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    moveActive(1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    moveActive(-1);
  } else if (event.key === 'Enter') {
    event.preventDefault();
    const option = filteredOptions.value[activeIndex.value];
    if (option) selectOption(option);
  }
};

watch(
  () => props.disabled,
  disabled => {
    if (disabled) isOpen.value = false;
  }
);

onBeforeUnmount(unbindGlobalListeners);
</script>

<template>
  <div ref="rootRef" class="agent-dropdown relative w-full min-w-0">
    <button
      ref="triggerRef"
      type="button"
      :disabled="disabled"
      :aria-label="ariaLabel || placeholder"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      class="agent-dropdown__trigger reset-base flex h-11 w-full items-center gap-2 rounded-lg border border-solid bg-n-solid-1 px-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60"
      :class="[
        hasError
          ? 'agent-dropdown__trigger--error border-n-ruby-8'
          : isOpen
            ? 'agent-dropdown__trigger--open border-n-brand'
            : 'border-n-weak hover:border-n-slate-6',
      ]"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <span
        v-if="selectedOption?.dotClass"
        class="agent-dropdown__trigger-dot size-2 flex-shrink-0 rounded-full"
        :class="selectedOption.dotClass"
      />
      <span
        class="agent-dropdown__trigger-label min-w-0 flex-1 truncate"
        :class="selectedOption ? 'text-n-slate-12' : 'text-n-slate-9'"
      >
        {{ triggerLabel }}
      </span>
      <span
        class="agent-dropdown__trigger-icon size-4 flex-shrink-0 text-n-slate-11"
        :class="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      />
    </button>

    <!-- Ancorado no proprio campo (top-full): abre sempre logo abaixo do input,
    acompanhando o scroll do modal sem calculo de posicao. -->
    <div
      v-if="isOpen"
      role="listbox"
      class="agent-dropdown__panel scrollbar-thin absolute left-0 top-full z-50 mt-1 flex max-h-64 w-full flex-col overflow-hidden rounded-lg border border-solid border-n-weak bg-n-solid-1 shadow-lg"
    >
      <div
        v-if="showSearch"
        class="agent-dropdown__search border-b border-n-weak p-2"
      >
        <input
          ref="searchRef"
          v-model="searchQuery"
          type="search"
          :placeholder="searchPlaceholder"
          class="agent-dropdown__search-input reset-base m-0 h-9 w-full rounded-lg border border-solid border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-9 focus:border-n-brand"
        />
      </div>

      <div
        class="agent-dropdown__options scrollbar-thin flex-1 overflow-y-auto p-1.5"
      >
        <button
          v-for="(option, index) in filteredOptions"
          :key="String(option.value)"
          type="button"
          role="option"
          :aria-selected="option.value === modelValue"
          :disabled="option.disabled"
          class="agent-dropdown__option reset-base flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-n-alpha-1 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-n-alpha-2"
          :class="{
            'agent-dropdown__option--active bg-n-alpha-1 dark:bg-n-alpha-2':
              index === activeIndex,
          }"
          @mouseenter="activeIndex = index"
          @click="selectOption(option)"
        >
          <span
            v-if="option.dotClass"
            class="agent-dropdown__option-dot size-2 flex-shrink-0 rounded-full"
            :class="option.dotClass"
          />
          <span
            class="agent-dropdown__option-label min-w-0 flex-1 truncate text-sm text-n-slate-12"
          >
            {{ option.label }}
          </span>
          <span
            v-if="option.value === modelValue"
            class="agent-dropdown__option-check i-lucide-check size-4 flex-shrink-0 text-n-brand"
          />
        </button>

        <p
          v-if="!filteredOptions.length"
          class="agent-dropdown__empty m-0 px-2.5 py-3 text-sm text-n-slate-11"
        >
          {{ emptyText }}
        </p>
      </div>
    </div>
  </div>
</template>
